import i18n from '../lang/i18n'
import { getBoxes } from '../redux/actions'

const renderAddressString = (address) => {
  if (!address) return 'Tom address'

  return `${address.street}, ${address.zip} ${address.city}`
}

const renderShortAddressString = (address) => {
  if (!address) return 'Empty address'

  return `${address.street}`
}

const statusTransitionServiceTranslation = (serviceChain = '') => {
  if (typeof serviceChain !== 'string' || !serviceChain) return 'N/A'

  const parsedService = serviceChain
    .split(' -> ')[0]
    .replaceAll('_', '-')
    .toLowerCase()

  switch (parsedService) {
    case 'service-integration':
    case 'service-delivery-api':
      return 'API'
    case 'parcel-manager':
      return 'Parcel Manager'
    case 'consumer':
    case 'service-consumer':
      return 'APP Consumer'
    case 'appsupport':
    case 'service-appsupport':
      return 'APP Driver'
    case 'iadmin':
      return 'Admin'
    case 'retool/iboxen':
    case 'retool/dynava':
      return 'iBoxen Backoffice'
    case 'retool/matka':
      return 'Matka Backoffice'
    default:
  }

  return parsedService
}

/**
 * Who the log is about. Every log names its actor the same way — "driver: bcibrahos70",
 * "carrier: Matkahuolto" — so the prefix says which kind of actor it was and this says which one.
 * It used to read only the driver, which left a Finnish collection with no name against it.
 */
const getActorFromLog = (log) => {
  if (typeof log !== 'string') return 'N/A'
  return log.split(/^[a-zA-Z]+:\s*([^,]+)/)?.[1]?.trim() || 'N/A'
}

/**
 * What the driver wrote on a log, when they wrote anything.
 *
 * Only a reason containing the words "wrong location" used to be shown. Every other one was
 * dropped — including whatever a driver types on a custom-reason deviation, which is the single
 * reason nobody can work out from the log's own wording, since the wording for it is "free text".
 */
const logReason = (reason) => {
  const text = String(reason ?? '').trim()
  return text ? ` | ${text}` : ''
}

const formatParcelLogs = async (parcelParam, dispatch, t, type = 'driver') => {
  const logs = []
  const parcel = parcelParam
  const types = type.split('|')
  let tableLogs = (parcel.logs || []).filter((log) => {
    return types.some((tt) => {
      if (log.value?.startsWith(`${tt}:`)) {
        return log
      }
      return false
    })
  })
  tableLogs = tableLogs.map((log) => {
    const prefix = log.value?.split(':')[0]
    return {
      ...log,
      // Finland's collection goes through Matkahuolto, so its logs say "carrier:" where a Swedish
      // one says "consumer:". They are the same event and read from the same wording; the actor
      // below still carries whichever name the log actually gave.
      logType: prefix === 'carrier' ? 'consumer' : prefix,
    }
  })

  // Filter out reallocations that were not made by the driver.
  const tableReallocations = (parcel.reallocations || [])
    .map((realloc) => {
      const driverLog = tableLogs.find(
        (log) =>
          log.type === 'payloads-create' &&
          new Date(log.created) - new Date(realloc.created) <= 10000 // payloads-create will always be created right after a reallocation from the driver
      )
      if (driverLog) {
        return {
          ...realloc,
          driver: getActorFromLog(driverLog.value),
        }
      }
      return realloc
    })
    .filter((realloc) => Boolean(realloc.driver))

  tableLogs.forEach((v) => {
    if (
      v.logType !== 'service' &&
      i18n.exists(`parcels:${v.logType}LogsMap.${v.type}`)
    ) {
      const reason = logReason(v.reason)

      const log = {
        log: `${t(`${v.logType}LogsMap.${v.type}`)}${
          v.inputMethod ? ` (${v.inputMethod}) ` : ' '
        }${`${v.isDoorClosed === false ? `- ${t('open')}` : ''}${
          v.isDoorClosed ? `- ${t('allClosed')}` : ''
        }`}${reason}`,
        created: v.created,
        friendlyDate: window.niceDate2(v.created),
        driver: getActorFromLog(v.value),
        logType: v.logType,
      }

      logs.push(log)
    }
    if (v.logType === 'service') {
      const log = {
        log: v.type,
        created: v.created,
        friendlyDate: window.niceDate2(v.created),
        logType: v.logType,
      }

      logs.push(log)
    }
  })

  // eslint-disable-next-line no-plusplus
  if (tableReallocations.length) {
    const boxIds = tableReallocations.flatMap((v) => [v.fromBoxId, v.toBoxId])
    const res = await dispatch(getBoxes(boxIds))
    const { boxes } = res.response || {}

    const boxesGrouped = tableReallocations.map((v) => {
      const fromBox = boxes.find((box) => box._id === v.fromBoxId)
      const toBox = boxes.find((box) => box._id === v.toBoxId)
      return { fromBox, toBox, created: v.created, driver: v.driver }
    })

    boxesGrouped.forEach(({ fromBox, toBox, created, driver }) => {
      const log = {
        created,
        friendlyDate: window.niceDate2(created),
        driver,
        // tagged so the merged log table can tell these apart: it takes reallocations from
        // parcel.reallocations, which is the complete list, and these are a subset of the same
        // events phrased differently
        logType: 'reallocation',
      }
      if (boxes) {
        log.log = `Parcel reallocated from ${fromBox.port + 1 + fromBox.mark}(${
          fromBox.abbreviation
        }) to ${toBox.port + 1 + toBox.mark}(${toBox.abbreviation})`
      } else {
        log.log = 'Parcel reallocated'
      }
      logs.push(log)
    })
  }

  const logStatuses = logs.sort(
    (a, b) => new Date(a.friendlyDate) - new Date(b.friendlyDate)
  )

  return logStatuses
}

export {
  formatParcelLogs,
  logReason,
  renderAddressString,
  renderShortAddressString,
  statusTransitionServiceTranslation,
}
